import { useEffect, useState } from "react";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, del, can } from "@/utils/service";
import { apiMessage } from "@/utils/api";
import { Panel, Field, input, primary, secondary, danger } from "./shared";

interface Role { id: string; key: string | null; name: string; description: string | null; is_system: boolean; memberships_count: number; permissions: string[] }
interface Module { module: string; label: string; permissions: { key: string; action: string }[] }

const ACTION_LABELS: Record<string, string> = {
  view: "See own & shared", view_all: "See all", create: "Create", update: "Edit", delete: "Delete", manage_all: "Manage anyone's", export: "Export",
  convert: "Convert", approve: "Approve", pay: "Pay out", refund: "Refund", invite: "Invite", remove: "Remove", manage: "Manage",
  manage_categories: "Manage categories", configure: "Configure taxes & methods",
};

/** Roles and what they allow. Built-in roles can be adjusted; the Owner role always has everything. */
export default function Roles() {
  const editable = can("roles.manage");
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [selected, setSelected] = useState<Role | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string; permissions: Set<string> } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (keep?: string) => {
    const [r, c] = await Promise.all([get<{ data: { roles: Role[] } }>("/roles"), get<{ data: { modules: Module[] } }>("/permissions").catch(() => ({ data: { modules: [] as Module[] } }))]);
    setRoles(r.data.roles);
    setModules(c.data.modules);
    choose(r.data.roles.find((x) => x.id === keep) ?? null);
  };
  useEffect(() => { load().catch((e) => notify(apiMessage(e, "Could not load roles."))); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (r: Role | null) => {
    setSelected(r);
    setDraft(r ? { name: r.name, description: r.description ?? "", permissions: new Set(r.permissions) } : null);
  };

  const startNew = () => {
    setSelected(null);
    setDraft({ name: "", description: "", permissions: new Set(["organization.view", "members.view"]) });
  };

  const toggle = (key: string, on: boolean) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    if (on) next.add(key); else next.delete(key);
    setDraft({ ...draft, permissions: next });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    const body = { name: draft.name, description: draft.description || null, permissions: [...draft.permissions] };
    try {
      if (selected) {
        await put(`/roles/${selected.id}`, body);
        notify("Role saved. Members with this role get the new permissions on their next action.");
        load(selected.id);
      } else {
        const r = await post<{ data: { role: Role } }>("/roles", body);
        notify("Role created");
        load(r.data.role.id);
      }
    } catch (e) { notify(apiMessage(e, "Could not save the role.")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected || !await confirmDialog(`Delete the role "${selected.name}"?`)) return;
    try { await del(`/roles/${selected.id}`); notify("Role deleted"); load(); }
    catch (e) { notify(apiMessage(e, "Could not delete the role.")); }
  };

  const isOwner = selected?.key === "owner";

  return (
    <Panel title="Roles & Permissions" description="What each role can see and do in this organization" actions={editable && <button onClick={startNew} className={secondary}>New role</button>}>
      {!draft ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map((r) => {
            const full = r.key === "owner" || r.permissions.includes("*");
            const areas = modules.filter((m) => m.permissions.some((p) => full || r.permissions.includes(p.key))).map((m) => m.label);
            return (
              <button key={r.id} onClick={() => choose(r)} className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-primary/40 hover:shadow-sm">
                <div className="flex w-full items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">{r.name}</p>
                  {r.is_system && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">Built-in</span>}
                </div>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2">{r.description ?? "Custom role"}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {full ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">Full access</span>
                    : areas.slice(0, 5).map((a) => <span key={a} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{a}</span>)}
                  {!full && areas.length > 5 && <span className="px-1 text-[11px] text-gray-500">+{areas.length - 5} more</span>}
                </div>
                <div className="min-h-[1rem] flex-1" />
                <div className="flex w-full items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
                  <span>{r.memberships_count} member{r.memberships_count === 1 ? "" : "s"}</span>
                  <span>{full ? "Everything" : `${r.permissions.length} permission${r.permissions.length === 1 ? "" : "s"}`}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
      <div className="space-y-4">
        <button onClick={() => choose(null)} className="text-xs text-primary hover:underline">← All roles</button>
        <h3 className="text-sm font-semibold text-gray-900">{selected ? selected.name : "New role"}</h3>
        {draft && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Name"><input className={input} disabled={!editable || isOwner} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
              <Field label="Description"><input className={input} disabled={!editable || isOwner} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
            </div>
            {isOwner && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">The Owner role always has every permission and can't be changed.</p>}
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {modules.map((m) => (
                <div key={m.module} className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-start">
                  <p className="w-44 shrink-0 text-xs font-semibold text-gray-900">{m.label}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {m.permissions.map((p) => (
                      <label key={p.key} className="flex items-center gap-1.5 text-xs text-gray-700">
                        <input type="checkbox" className="accent-primary" disabled={!editable || isOwner} checked={isOwner || draft.permissions.has(p.key)} onChange={(e) => toggle(p.key, e.target.checked)} />
                        {ACTION_LABELS[p.action] ?? p.action}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {editable && !isOwner && (
              <div className="flex justify-end gap-2">
                {selected && !selected.is_system && <button onClick={remove} className={danger}>Delete role</button>}
                <button onClick={save} disabled={busy || !draft.name} className={primary}>{busy ? "Saving..." : selected ? "Save role" : "Create role"}</button>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </Panel>
  );
}
